import { createContext } from "react";
import type { TourKeyOverrides, TourRun } from "./tourEngine";

export interface PageRegistration {
  ready: boolean;
  hasData: boolean;
}

export interface ActiveTour {
  run: TourRun;
  stepIndex: number;
  /**
   * Keys of the steps this run has already had their press for: the coach's, on a
   * `ClickToContinue` step, or the tour's own, on an `OpenAnchor` one.
   *
   * The one record every direction reads: the overlay renders such a step as read-and-Next rather
   * than asking for the press again, `useStepInteraction` will not press it a second time, Back
   * walks past it, and the end of the run closes again what those presses opened. Per run, because
   * it is about what the page now looks like, not about what the coach has ever seen.
   */
  satisfiedStepKeys: string[];
}

/**
 * A replay asked for from somewhere other than the page itself (the Guided tours list), waiting
 * for that page's `useTour` to run it. `pageKey` is the resolved page tour key, so a second view of
 * the same feature (schedule's catalog) never takes a request meant for the feature's own page.
 */
export interface ReplayRequest {
  featureKey: string;
  pageKey: string;
  /** The pathname the tour runs on. Leaving for any other pathname drops the request. */
  path: string;
  /** The entry said `dataTourKey: null`: replay its tour alone, with no data tour after it. */
  standalone: boolean;
}

/** "disabled" covers an API without tours, a network failure, and a paused organization. */
export type TourConfigStatus = "idle" | "loading" | "ready" | "disabled";

export interface TourContextValue {
  status: TourConfigStatus;
  /**
   * Calls `callback` once the route has settled, which is a frame after a navigation so a tour
   * never starts against the old page: straight away when it already has. Returns a cancel function.
   *
   * A subscription rather than a boolean on this value on purpose. Every search-param change (a
   * filter, a page of a table) is a navigation, and a boolean that flipped false then true would
   * re-render every consumer of this context twice for each one, whole tables included.
   */
  whenRouteSettled: (callback: () => void) => () => void;
  /**
   * The current pathname. Search-param changes do not change it, so it only re-renders consumers
   * when the user actually goes to another page.
   */
  pathname: string;
  activeTour: ActiveTour | null;
  /**
   * Whether the page or data tour `replayFeature` would run (resolved from `keys`, defaulting to
   * `${feature}.page` / `${feature}.data`) has a step this user can see.
   */
  hasTours: (featureKey: string, keys?: TourKeyOverrides) => boolean;
  /**
   * Whether the page tour alone (resolved from `keys`) has a step this user can see. A requested
   * replay always starts there, so this, not `hasTours`, decides what the Guided tours list offers.
   */
  hasPageTour: (featureKey: string, keys?: TourKeyOverrides) => boolean;
  /** The run this tour is due for right now, or null. */
  getDueRun: (tourKey: string) => TourRun | null;
  updatePage: (featureKey: string, registration: PageRegistration) => void;
  unregisterPage: (featureKey: string) => void;
  /** Starts a tour by key. `replay` runs every renderable step and records nothing. */
  startTour: (tourKey: string, options?: { replay?: boolean }) => void;
  startRun: (run: TourRun) => void;
  /** Runs after the active tour ends, if it is still due then. */
  queueRun: (run: TourRun) => void;
  /** The page tour, then the data tour when the page has data. Records nothing. */
  replayFeature: (featureKey: string, keys?: TourKeyOverrides) => void;
  /** The run `replayFeature` would start right now, or null when it would start nothing. */
  getReplayRun: (featureKey: string, keys?: TourKeyOverrides) => TourRun | null;
  /** The replay waiting for its page, or null. */
  pendingReplay: ReplayRequest | null;
  /**
   * Asks the page at `path` to replay its tour once it is ready, the route has settled and no
   * dialog holds it (the same gates as auto-start). The caller navigates; this only leaves the note.
   * Records nothing, exactly like `replayFeature`.
   */
  requestReplay: (featureKey: string, keys: TourKeyOverrides | undefined, path: string) => void;
  /** Drops `request` if it is still the pending one. Called by the hook that consumes it. */
  clearReplay: (request: ReplayRequest) => void;
  next: () => void;
  back: () => void;
  skip: () => void;
  finish: () => void;
}

/**
 * Null outside `TourProvider`. Consumers treat that as "no tours" rather than throwing, so a page
 * rendered in isolation (a unit test, a storybook-style harness) behaves exactly as it did before
 * tours existed.
 */
export const TourContext = createContext<TourContextValue | null>(null);
